import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onDestroy } from 'svelte';
import * as pipelineModel from '../../../../features/pipeline/pipeline-model.ts';
import { getLanguage, subscribeLanguageChange, t } from '../../i18n.ts';
import { LIGHT_PRESETS } from '../../ui/preview-presets.ts';
import DropdownMenu from '../svelte-dropdown-menu.svelte';

import {
	clamp,
	cloneLightSettings,
	getLightRangeStep,
	isLightAngleBinding,
	isValidLightSettings
} from './shared.ts';

var root = $.from_html(`<span class="ui-symbol-kebab svelte-vr5bek">...</span>`);
var root_1 = $.from_html(`<button type="button" class="ui-menu-item svelte-vr5bek" role="menuitem"> </button>`);
var root_2 = $.from_html(`<div class="ui-menu-header svelte-vr5bek"> </div> <!>`, 1);
var root_3 = $.from_html(`<label class="field svelte-vr5bek"><span class="label-row svelte-vr5bek"><span class="label-text svelte-vr5bek"> </span> <span class="value-text svelte-vr5bek"><!></span></span> <input class="range-input svelte-vr5bek" type="range"/></label>`);
var root_4 = $.from_html(`<div class="panel-root svelte-vr5bek"><div class="panel-head svelte-vr5bek"><div><div class="section-label"> </div> <div class="help-text svelte-vr5bek"> </div></div> <div class="light-actions svelte-vr5bek"><button type="button" class="guide-button svelte-vr5bek" id="btn-toggle-light-gizmo"> </button> <!></div></div> <div class="grid svelte-vr5bek"><label class="field color-field svelte-vr5bek"><span class="label-row svelte-vr5bek"><span class="label-text svelte-vr5bek"> </span> <span class="value-text svelte-vr5bek" id="light-color-value"> </span></span> <input class="color-input svelte-vr5bek" type="color" id="light-color"/></label> <label class="field color-field svelte-vr5bek"><span class="label-row svelte-vr5bek"><span class="label-text svelte-vr5bek"> </span> <span class="value-text svelte-vr5bek" id="light-ambient-color-value"> </span></span> <input class="color-input svelte-vr5bek" type="color" id="light-ambient-color"/></label> <!></div></div>`);

const $$css = {
	hash: 'svelte-vr5bek',
	code: '.panel-root.svelte-vr5bek {display:flex;flex-direction:column;gap:10px;}.panel-head.svelte-vr5bek {display:flex;justify-content:space-between;gap:8px;}.help-text.svelte-vr5bek {font-size:11px;color:var(--color-muted);}.grid.svelte-vr5bek {display:grid;grid-template-columns:repeat(2, minmax(0, 1fr));gap:8px;}.field.svelte-vr5bek {display:flex;flex-direction:column;gap:6px;padding:9px 10px;border:1px solid var(--color-panel-border-strong, #464646);border-radius:12px;background:color-mix(in srgb, var(--color-panel), var(--color-bg) 4%);}.color-field.svelte-vr5bek {grid-column:span 2;}.label-row.svelte-vr5bek {display:flex;align-items:center;justify-content:space-between;gap:10px;}.label-text.svelte-vr5bek {font-size:11px;font-weight:700;color:var(--color-text-strong);}.value-text.svelte-vr5bek {font-family:var(--font-mono, monospace);font-size:11px;color:var(--color-muted);}.light-actions.svelte-vr5bek {display:inline-flex;align-items:center;gap:8px;}.light-actions.svelte-vr5bek > :where(.svelte-vr5bek) {flex-shrink:0;}.guide-button.svelte-vr5bek {border:1px solid var(--color-line);background:var(--color-panel);border-radius:8px;padding:6px 11px;font-size:12px;color:var(--color-muted);cursor:pointer;transition:120ms ease;}.guide-button.svelte-vr5bek:hover {border-color:var(--color-accent);transform:translateY(-1px);}.guide-button[aria-pressed="true"].svelte-vr5bek {border-color:var(--color-accent);color:color-mix(in srgb, var(--color-text-strong), var(--color-accent) 24%);}.color-input.svelte-vr5bek {width:100%;height:34px;border:1px solid var(--color-control-border, var(--color-line));border-radius:9px;background:var(--color-control-bg, var(--color-panel));padding:4px;}.range-input.svelte-vr5bek {width:100%;accent-color:var(--color-accent);}\n\n  @media (max-width: 1180px) {.grid.svelte-vr5bek {grid-template-columns:1fr;}.color-field.svelte-vr5bek {grid-column:span 1;}\n  }\n\n  @media (max-width: 900px) {.panel-head.svelte-vr5bek {flex-direction:column;align-items:flex-start;}.light-actions.svelte-vr5bek {width:100%;justify-content:space-between;}\n  }'
};

export default function Svelte_light_panel($$anchor, $$props) {
	$.push($$props, true);
	$.append_styles($$anchor, $$css);

	const settings = $.prop($$props, 'settings', 23, () => cloneLightSettings(pipelineModel.DEFAULT_LIGHT_SETTINGS));

	function dispatch(eventName, detail) {
		$$props.$$host.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
	}

	let language = $.state($.proxy(getLanguage()));

	const disposeLanguageSync = subscribeLanguageChange((nextLanguage) => {
		$.set(language, nextLanguage, true);
	});

	onDestroy(() => {
		disposeLanguageSync();
	});

	function tr(key, values) {
		$.get(language);

		return values ? t(key, values) : t(key);
	}

	function handleColorInput(event, kind) {
		const input = event.currentTarget;

		if (!input) {
			dispatch('status-message', {
				message: tr(kind === 'light'
					? 'panel.lightColorInputMissing'
					: 'panel.ambientColorInputMissing'),
				kind: 'error'
			});

			return;
		}

		const parsed = pipelineModel.parseHexColor(input.value);

		if (!parsed) {
			dispatch('status-message', {
				message: tr(kind === 'light'
					? 'panel.lightColorInvalid'
					: 'panel.ambientColorInvalid'),
				kind: 'error'
			});

			return;
		}

		dispatch('settings-change', {
			settings: {
				...settings(),
				...kind === 'light'
					? { lightColor: [parsed[0], parsed[1], parsed[2]] }
					: { ambientColor: [parsed[0], parsed[1], parsed[2]] }
			}
		});
	}

	function handleRangeInput(event, binding) {
		const input = event.currentTarget;

		if (!input) {
			dispatch('status-message', {
				message: tr('panel.rangeInputMissing', { label: binding.label }),
				kind: 'error'
			});

			return;
		}

		const parsed = Number(input.value);

		if (!Number.isFinite(parsed)) {
			dispatch('status-message', {
				message: tr('panel.rangeInvalid', { label: binding.label }),
				kind: 'error'
			});

			return;
		}

		const next = cloneLightSettings(settings());

		next[binding.key] = clamp(parsed, binding.min, binding.max);
		dispatch('settings-change', { settings: next });
	}

	function handleLightRangeWheel(event, binding) {
		event.preventDefault();

		const step = Number(getLightRangeStep(binding));
		const delta = event.deltaY < 0 ? step : -step;
		const next = cloneLightSettings(settings());

		next[binding.key] = clamp(settings()[binding.key] + delta, binding.min, binding.max);
		dispatch('settings-change', { settings: next });
	}

	function toggleGizmo() {
		dispatch('settings-change', {
			settings: { ...settings(), showGizmo: !settings().showGizmo }
		});
	}

	function applyLightPreset(preset) {
		if (!isValidLightSettings(preset.settings)) {
			dispatch('status-message', {
				message: tr('panel.status.lightPresetInvalidValue', { value: preset.key }),
				kind: 'error'
			});

			return false;
		}

		dispatch('settings-change', { settings: cloneLightSettings(preset.settings) });

		dispatch('status-message', {
			message: tr('panel.status.lightPresetApplied', { name: tr(preset.labelKey) }),
			kind: 'info'
		});

		return true;
	}

	const lightColorHex = $.derived(() => pipelineModel.colorToHex(settings().lightColor));
	const ambientColorHex = $.derived(() => pipelineModel.colorToHex(settings().ambientColor));

	var $$exports = {
		get settings() {
			return settings();
		},

		set settings(
			$$value = cloneLightSettings(pipelineModel.DEFAULT_LIGHT_SETTINGS)
		) {
			settings($$value);
			$.flush();
		}
	};

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.key(node, () => $.get(language), ($$anchor) => {
		var div = root_4();
		var div_1 = $.child(div);
		var div_2 = $.child(div_1);
		var div_3 = $.child(div_2);
		var text = $.only_child(div_3, true);
		var div_4 = $.sibling(div_3, 2);
		var text_1 = $.only_child(div_4, true);

		$.reset(div_2);

		var div_5 = $.sibling(div_2, 2);
		var button = $.child(div_5);
		var text_2 = $.only_child(button, true);
		var node_1 = $.sibling(button, 2);

		{
			const trigger = ($$anchor) => {
				var span = root();

				$.append($$anchor, span);
			};

			const children = ($$anchor, closeMenu = $.noop) => {
				var fragment_1 = root_2();
				var div_6 = $.first_child(fragment_1);
				var text_3 = $.only_child(div_6, true);
				var node_2 = $.sibling(div_6, 2);

				$.each(node_2, 17, () => LIGHT_PRESETS, (preset) => preset.labelKey, ($$anchor, preset) => {
					var button_1 = root_1();
					var text_4 = $.only_child(button_1, true);

					$.template_effect(($0) => $.set_text(text_4, $0), [() => tr($.get(preset).labelKey)]);

					$.delegated('click', button_1, () => {
						if (applyLightPreset($.get(preset))) {
							closeMenu()();
						}
					});

					$.append($$anchor, button_1);
				});

				$.template_effect(($0) => $.set_text(text_3, $0), [() => tr('panel.lightPreset')]);
				$.append($$anchor, fragment_1);
			};

			let $0 = $.derived(() => tr('panel.lightPreset'));

			DropdownMenu(node_1, {
				wrapperClass: 'ui-menu-wrap',
				triggerClassName: 'ui-menu-trigger',
				menuClass: 'ui-menu',
				get triggerAriaLabel() {
					return $.get($0);
				},
				menuRole: 'menu',
				triggerVariant: 'menu-trigger',
				trigger,
				children,
				$$slots: { trigger: true, default: true }
			});
		}

		$.reset(div_5);
		$.reset(div_1);

		var div_7 = $.sibling(div_1, 2);
		var label = $.child(div_7);
		var span_1 = $.child(label);
		var span_2 = $.child(span_1);
		var text_5 = $.only_child(span_2, true);
		var span_3 = $.sibling(span_2, 2);
		var text_6 = $.only_child(span_3, true);

		$.reset(span_1);

		var input_1 = $.sibling(span_1, 2);

		$.remove_input_defaults(input_1);
		$.reset(label);

		var label_1 = $.sibling(label, 2);
		var span_4 = $.child(label_1);
		var span_5 = $.child(span_4);
		var text_7 = $.only_child(span_5, true);
		var span_6 = $.sibling(span_5, 2);
		var text_8 = $.only_child(span_6, true);

		$.reset(span_4);

		var input_2 = $.sibling(span_4, 2);

		$.remove_input_defaults(input_2);
		$.reset(label_1);

		var node_3 = $.sibling(label_1, 2);

		$.each(node_3, 17, () => pipelineModel.LIGHT_RANGE_BINDINGS, (binding) => binding.key, ($$anchor, binding) => {
			var label_2 = root_3();
			var span_7 = $.child(label_2);
			var span_8 = $.child(span_7);
			var text_9 = $.only_child(span_8, true);
			var span_9 = $.sibling(span_8, 2);
			var node_4 = $.child(span_9);

			{
				var consequent = ($$anchor) => {
					var text_10 = $.text();

					$.template_effect(($0) => $.set_text(text_10, `${$0 ?? ''}°`), [
						() => settings()[$.get(binding).key].toFixed($.get(binding).fractionDigits)
					]);

					$.append($$anchor, text_10);
				};

				var d = $.derived(() => isLightAngleBinding($.get(binding)));

				var alternate = ($$anchor) => {
					var text_11 = $.text();

					$.template_effect(($0) => $.set_text(text_11, $0), [
						() => settings()[$.get(binding).key].toFixed($.get(binding).fractionDigits)
					]);

					$.append($$anchor, text_11);
				};

				$.if(node_4, ($$render) => {
					if ($.get(d)) $$render(consequent); else $$render(alternate, -1);
				});
			}

			$.reset(span_9);
			$.reset(span_7);

			var input_3 = $.sibling(span_7, 2);

			$.remove_input_defaults(input_3);
			$.reset(label_2);

			$.template_effect(
				($0, $1, $2, $3) => {
					$.set_text(text_9, $.get(binding).label);
					$.set_attribute(span_9, 'id', $.get(binding).outputId);
					$.set_attribute(input_3, 'id', $.get(binding).inputId);
					$.set_attribute(input_3, 'min', $0);
					$.set_attribute(input_3, 'max', $1);
					$.set_attribute(input_3, 'step', $2);
					$.set_value(input_3, $3);
				},
				[
					() => String($.get(binding).min),
					() => String($.get(binding).max),
					() => getLightRangeStep($.get(binding)),
					() => String(settings()[$.get(binding).key])
				]
			);

			$.delegated('input', input_3, (event) => handleRangeInput(event, $.get(binding)));
			$.delegated('change', input_3, (event) => handleRangeInput(event, $.get(binding)));
			$.event('wheel', input_3, (event) => handleLightRangeWheel(event, $.get(binding)));
			$.append($$anchor, label_2);
		});

		$.reset(div_7);
		$.reset(div);

		$.template_effect(
			($0, $1, $2, $3, $4) => {
				$.set_text(text, $0);
				$.set_text(text_1, $1);
				$.set_attribute(button, 'aria-pressed', settings().showGizmo ? 'true' : 'false');
				$.set_text(text_2, $2);
				$.set_text(text_5, $3);
				$.set_text(text_6, $.get(lightColorHex));
				$.set_value(input_1, $.get(lightColorHex));
				$.set_text(text_7, $4);
				$.set_text(text_8, $.get(ambientColorHex));
				$.set_value(input_2, $.get(ambientColorHex));
			},
			[
				() => tr('panel.lightTitle'),
				() => tr('panel.lightHelp'),
				() => tr('panel.guide'),
				() => tr('panel.lightColor'),
				() => tr('panel.ambientColor')
			]
		);

		$.delegated('click', button, toggleGizmo);
		$.delegated('input', input_1, (event) => handleColorInput(event, 'light'));
		$.delegated('input', input_2, (event) => handleColorInput(event, 'ambient'));
		$.append($$anchor, div);
	});

	$.append($$anchor, fragment);

	return $.pop($$exports);
}

$.delegate(['click', 'input', 'change']);
customElements.define('lut-light-panel', $.create_custom_element(Svelte_light_panel, { settings: {} }, [], []));